import { Stack, Typography } from '@mui/material';
import { useCallback } from 'react';

import {
  type FeedbackRating,
  FeedbackRatingButtons,
} from '@/components/form/baseInputs/Button/FeedbackRatingButtons';
import { useRateSummary } from '@/modules/documents/hooks/useRateSummary';
import {
  DocumentSummaryAiSummaryRating,
  DocumentSummaryEntryMode,
} from '@/types/schema';

import { UserFeedbackModal } from '../../components/modals/UserFeedbackModal/UserFeedbackModal';

interface RateSummaryProps {
  documentSummaryId: string;
  documentSummaryEntryMode?: DocumentSummaryEntryMode | null;
  allowFeedback: boolean;
  existingRating: DocumentSummaryAiSummaryRating | null;
}

export function RateSummary({
  documentSummaryId,
  documentSummaryEntryMode,
  allowFeedback,
  existingRating,
}: RateSummaryProps) {
  const useRateSummaryMethods = useRateSummary(
    documentSummaryId,
    existingRating,
    allowFeedback
  );
  const { upRateSummary, downRateSummary, loading } = useRateSummaryMethods;

  const rate = useCallback(
    (rating: FeedbackRating) => {
      if (rating === 'good') {
        upRateSummary();
      } else {
        void downRateSummary();
      }
    },
    [downRateSummary, upRateSummary]
  );

  let feedbackRating: FeedbackRating | null = null;
  if (existingRating === DocumentSummaryAiSummaryRating.Good) {
    feedbackRating = 'good';
  } else if (existingRating === DocumentSummaryAiSummaryRating.Bad) {
    feedbackRating = 'bad';
  }

  if (
    documentSummaryEntryMode !== DocumentSummaryEntryMode.AiAuto &&
    documentSummaryEntryMode !== DocumentSummaryEntryMode.AiUserInvoked
  ) {
    return null;
  }

  return (
    <>
      <UserFeedbackModal
        isFeedbackModalOpen={useRateSummaryMethods.isFeedbackModalOpen}
        closeFeedbackModal={useRateSummaryMethods.closeFeedbackModal}
        provideFeedback={useRateSummaryMethods.provideFeedback}
        headingText="Provide feedback"
        placeholderText="How could this summary be improved?"
        helpText="This summary was generated using Luminary AI. We are continuously improving our document summary capabilities based on feedback."
      />
      <Stack direction="row" spacing={1}>
        <Stack justifyContent="center">
          <Typography variant="body-xs">Rate AI summary</Typography>
        </Stack>
        <Stack direction="row" alignItems="center">
          <FeedbackRatingButtons
            rating={feedbackRating}
            disabled={loading}
            likeAriaLabel="I like this summary"
            dislikeAriaLabel="I dislike this summary"
            onRate={rate}
          />
        </Stack>
      </Stack>
    </>
  );
}
